"use client"

// Suggestion / Suggestions: starter-prompt chips for the empty state. Adapted
// from Vercel AI Elements (https://ai-sdk.dev/elements, suggestion),
// re-authored token-driven.

import { cn } from "@/lib/utils"
import type { ComponentProps, ReactNode } from "react"

/**
 * Horizontal, wrapping row of suggestion chips.
 *
 * @param className - extra classes merged onto the row.
 */
export function Suggestions({ className, ...props }: ComponentProps<"div">): ReactNode {
  return <div className={cn("flex flex-wrap justify-center gap-2", className)} {...props} />
}

interface SuggestionProps extends Omit<ComponentProps<"button">, "onClick" | "onSelect"> {
  /** The suggestion text; also the value passed to `onSelect`. */
  suggestion: string
  /** Invoked with the suggestion text when the chip is clicked. */
  onSelect: (suggestion: string) => void
}

/**
 * A single clickable suggestion chip.
 *
 * @param suggestion - the suggestion text/value.
 * @param onSelect - receives the suggestion when clicked.
 * @param children - chip contents (defaults to the suggestion text).
 */
export function Suggestion({
  suggestion,
  onSelect,
  className,
  children,
  ...props
}: SuggestionProps): ReactNode {
  return (
    <button
      type="button"
      onClick={() => onSelect(suggestion)}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border border-suggestion-border bg-background px-3.5 py-1.5 font-body text-sm text-suggestion-foreground transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className
      )}
      {...props}
    >
      {children ?? suggestion}
    </button>
  )
}
